/**
 * Adaptation réseau de la coquille native — point de passage UNIQUE.
 *
 * 🔴 POURQUOI CE MODULE EXISTE (2026-09-10)
 * L'app passait par `server.url` : elle affichait le site distant, donc l'origine du
 * document ÉTAIT `app.mondalon.net`. Deux choses marchaient toutes seules :
 *   1. les appels relatifs (`fetch('/api/...')`) visaient le bon serveur ;
 *   2. le cookie de session, posé sur `.mondalon.net`, partait avec chaque requête.
 *
 * En fichiers embarqués, l'origine devient `mondalon://localhost`. Les deux tombent :
 * les appels relatifs visent la coquille elle-même (404), et le cookie n'est plus
 * envoyé (401). L'app démarrerait sur un écran vide, sans message parlant.
 *
 * 🔴 POURQUOI UN POINT DE PASSAGE PLUTÔT QUE 29 CORRECTIONS
 * Le code porte 29 appels `fetch('/api/…')` répartis dans 16 fichiers. Les corriger un
 * par un demanderait de n'en oublier aucun — or un seul oubli = un écran cassé en
 * production, découvert par un utilisateur. Surtout, la correction ne tiendrait pas :
 * le prochain `fetch('/api/…')` écrit par quiconque casserait l'app en silence, et rien
 * ne le signalerait avant la prochaine réclamation.
 * Ici, tout appel présent ET futur est couvert sans que personne ait à y penser.
 * C'est le même choix que côté serveur (`readSessionCookie`, point de lecture unique) :
 * on rend la propriété STRUCTURELLE au lieu de la confier à la vigilance.
 *
 * ⚠️ SUR LE WEB, CE MODULE NE FAIT RIEN.
 * `VITE_API_URL` n'est défini QUE pour la construction native. Sur le web il est vide,
 * `installerReseauNatif()` sort immédiatement, et `fetch` n'est jamais remplacé — le
 * comportement du navigateur reste strictement identique à aujourd'hui (cookie compris).
 */

const BASE_API = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '')
const CLE_JETON = 'mondalon_session_token'

/** Range le jeton renvoyé à la connexion. Sans effet sur le web (le cookie y suffit). */
export function memoriserJeton(jeton: string | undefined | null): void {
  if (!BASE_API || !jeton) return
  try {
    localStorage.setItem(CLE_JETON, jeton)
  } catch {
    /* stockage indisponible (mode privé) — l'app retombera sur l'écran de connexion */
  }
}

/** Oublie le jeton (déconnexion, suppression de compte). */
export function oublierJeton(): void {
  try {
    localStorage.removeItem(CLE_JETON)
  } catch {
    /* rien à faire : le jeton est déjà inaccessible */
  }
}

function lireJeton(): string | null {
  try {
    return localStorage.getItem(CLE_JETON)
  } catch {
    return null
  }
}

/**
 * Remplace `fetch` pour les seules requêtes internes, une fois pour toutes.
 * À appeler AVANT le premier rendu (cf. `main.tsx`) : un appel parti avant
 * l'installation viserait la coquille et échouerait.
 */
export function installerReseauNatif(): void {
  if (!BASE_API) {
    // 🔴 GARDE-FOU — le pire scénario n'est pas l'erreur, c'est le SILENCE.
    // Un paquet natif construit avec `npm run build` au lieu de `npm run build:native`
    // n'a pas de base d'API : l'installation ne fait rien, tous les appels visent la
    // coquille, et l'app s'ouvre sur un écran vide sans qu'aucune erreur ne l'explique.
    // C'est exactement le motif « un dispositif qui ne fait rien et ne le dit pas »
    // rencontré trois fois cette semaine. Ici, au moins, il le dit.
    // Le protocole `capacitor:` (iOS) et l'hôte `localhost` (Android) ne peuvent pas
    // apparaître sur le web : aucune fausse alerte possible pour un visiteur.
    const proto = globalThis.location?.protocol || ''
    const hote = globalThis.location?.hostname || ''
    if (proto === 'capacitor:' || hote === 'localhost') {
      console.error(
        '[reseauNatif] Coquille native détectée SANS base d\'API. ' +
        'Ce paquet a été construit avec « npm run build » au lieu de « npm run build:native » : ' +
        'aucun appel n\'aboutira. Reconstruire avec la bonne commande.'
      )
    }
    return // web : rien à adapter
  }
  const fetchOrigine = globalThis.fetch.bind(globalThis)

  globalThis.fetch = (entree: RequestInfo | URL, options?: RequestInit) => {
    // 🔴 UNIQUEMENT `/api/` — corrigé en revue le 2026-09-10. La première version
    // réécrivait TOUT chemin commençant par « / », en affirmant s'appuyer sur une
    // vérification des appels du projet. Cette vérification ne portait en fait que sur
    // les appels d'API : `UpdateBanner.tsx` interroge `/version.json`, un fichier
    // EMBARQUÉ dans le paquet. Détourné vers le serveur, il aurait renvoyé la version du
    // WEB, forcément différente de celle figée dans le paquet natif dès le premier
    // déploiement — d'où un bandeau « Nouvelle version disponible » affiché en
    // permanence, dont le bouton « Rafraîchir » recharge le paquet local et ne change
    // rien. Un défaut visible par tout utilisateur, et impossible à faire disparaître.
    //
    // Les formes `URL` et `Request` sont volontairement NON traitées : elles n'existent
    // nulle part dans ce projet, et reconstruire une `Request` cross-origin risquerait
    // d'en vider le corps — donc de casser les envois de fichiers, qui sont justement la
    // raison d'être de la plupart de ces appels directs. Si l'une apparaît un jour, elle
    // passera intacte et échouera franchement, plutôt qu'à l'envoi d'une photo.
    //
    // ⚠️ Les images ne passent PAS par ici : `/uploads/…` est rendu en `<img src>`, une
    // ressource chargée par le DOM, hors de portée de `fetch`. Traité séparément — voir
    // le référentiel technique, étape 2.
    if (typeof entree !== 'string' || !entree.startsWith('/api/')) {
      return fetchOrigine(entree, options)
    }

    const cible = BASE_API + entree
    const jeton = lireJeton()

    // Les en-têtes déjà posés par l'appelant sont conservés ; on n'écrase jamais une
    // autorisation explicite (un appel qui porte déjà la sienne sait ce qu'il fait).
    const entetes = new Headers(options?.headers)
    if (jeton && !entetes.has('Authorization')) entetes.set('Authorization', `Bearer ${jeton}`)

    return fetchOrigine(cible, { ...options, headers: entetes }).then(absolutiserMedias)
  }
}

/**
 * Rend absolues les adresses de médias contenues dans une réponse JSON.
 *
 * 🔴 POURQUOI ICI, ET PAS DANS LES BALISES `<img>` (2026-09-10, trouvé en revue)
 * Les avatars et photos sont rendus par `<img src={user.avatarUrl}>` : une ressource
 * chargée par le DOM, que `fetch` ne voit jamais. En fichiers embarqués, un chemin
 * `/uploads/avatars/x.jpg` se résout contre la coquille — **tous les avatars et toutes
 * les photos cassés**.
 *
 * La correction évidente serait d'envelopper chaque `<img>` d'un helper. Mesuré avant de
 * choisir : le front ne construit **jamais** ces chemins lui-même (une seule occurrence
 * de « /uploads » dans tout `src/`, et c'est ce commentaire). Ils viennent **du serveur**,
 * portés par des champs (`avatarUrl`, `imageUrl`, `photoUrl`…) rendus dans au moins douze
 * balises. Les envelopper une à une exigerait de n'en oublier aucune — et le prochain
 * `<img>` ajouté par quiconque casserait de nouveau, sans que rien ne le signale.
 *
 * On corrige donc **à l'entrée des données**, seul endroit par lequel elles passent
 * toutes. Tout champ présent ET futur est couvert, sans toucher une seule balise.
 * Même principe que l'interception elle-même : structurel, pas déclaratif.
 *
 * ⚠️ Ne transforme QUE les chaînes commençant par `/uploads/` : ni les chemins de routes,
 * ni les adresses déjà absolues, ni les textes rédigés par les membres.
 */
function absolutiserMedias(reponse: Response): Response | Promise<Response> {
  const type = reponse.headers.get('content-type') || ''
  if (!type.includes('application/json')) return reponse

  return reponse
    .clone()
    .json()
    .then((donnees) => {
      const corrige = absolutiserChemins(donnees, BASE_API)
      return new Response(JSON.stringify(corrige), {
        status: reponse.status,
        statusText: reponse.statusText,
        headers: reponse.headers,
      })
    })
    // Corps illisible ou JSON malformé : on rend la réponse d'origine intacte. Une
    // normalisation de confort ne doit jamais faire échouer un appel qui aurait abouti.
    .catch(() => reponse)
}

/**
 * Réécrit récursivement toute chaîne commençant par `/uploads/` en la préfixant de `base`.
 * PURE (aucune entrée/sortie, aucune dépendance au contexte) — donc testable seule, et
 * c'est le seul endroit qui décide de ce qui est un média. Exportée pour cette raison.
 */
export function absolutiserChemins(valeur: unknown, base: string): unknown {
  if (typeof valeur === 'string') {
    return valeur.startsWith('/uploads/') ? base + valeur : valeur
  }
  if (Array.isArray(valeur)) return valeur.map((v) => absolutiserChemins(v, base))
  if (valeur && typeof valeur === 'object') {
    const sortie: Record<string, unknown> = {}
    for (const [cle, v] of Object.entries(valeur)) sortie[cle] = absolutiserChemins(v, base)
    return sortie
  }
  return valeur
}
